import React from "react";
import PropTypes from "prop-types";
import { displayDateUS } from "../../../utils/dateUtils";
import { formatPhone } from "../../Application/application.validation";
import {
  buildEmergencyContacts,
  buildDiagnosisAllergies,
  buildAllergies,
  buildReferrals,
  buildInsurances,
  buildPhysicians,
  formatAllergyNames,
} from "../patientForm.shared";
import {
  buildNutrition,
  NUTRITION_SECTION_TITLE,
  formatDietType,
  formatSupplementEnteral,
  formatSodiumRestriction,
  formatCarbohydrateRestriction,
} from "../patientNutrition.shared";
import { DiagnosisNutritionPrintContent } from "./DiagnosisNutritionSectionContent";
import { STATUS_LABELS, SOC_DATE_LABEL, EOC_DATE_LABEL, statusRequiresEocDate } from "./StatusBadge";

const dash = (v) => {
  const s = v == null ? "" : String(v).trim();
  return s || "—";
};

const PrintField = ({ label, value, className = "" }) => (
  <div className={`min-w-0 py-0.5 break-inside-avoid ${className}`}>
    <div className="text-[9px] font-semibold uppercase tracking-wide text-slate-500">
      {label}
    </div>
    <div className="mt-0.5 text-[12px] text-slate-900 font-medium break-words leading-snug">
      {dash(value)}
    </div>
  </div>
);

const PrintSection = ({ title, children }) => (
  <section className="break-inside-avoid border border-slate-300 rounded-md overflow-hidden mb-3">
    <div className="px-3 py-1.5 border-b border-slate-300 bg-slate-100">
      <h2 className="text-[11px] font-bold uppercase tracking-wide text-slate-800">
        {title}
      </h2>
    </div>
    <div className="px-3 py-2.5 bg-white">{children}</div>
  </section>
);

const FieldGrid = ({ children }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-x-4 gap-y-2">
    {children}
  </div>
);

const EmptyLine = ({ text }) => (
  <p className="text-[12px] text-slate-500 italic">{text}</p>
);

/**
 * Printable demographics sheet — information only (no edit/print chrome).
 * Mirrors the Demographics tab sections and field labels.
 */
const DemographicsPrintSheet = ({ patient, notes = [] }) => {
  const contacts = buildEmergencyContacts(patient);
  const diagnoses = buildDiagnosisAllergies(patient);
  const allergies = buildAllergies(patient);
  const allergyName = formatAllergyNames(allergies);
  const referrals = buildReferrals(patient);
  const insurances = buildInsurances(patient);
  const physicians = buildPhysicians(patient);
  const nutrition = buildNutrition(patient);

  const patientName =
    [patient?.lastName, patient?.firstName].filter(Boolean).join(", ") ||
    [patient?.firstName, patient?.middleName, patient?.lastName]
      .filter(Boolean)
      .join(" ") ||
    "Patient";

  return (
    <article className="mx-auto w-full max-w-4xl bg-white text-slate-900 px-4 sm:px-6 py-4 print:px-0 print:py-0">
      <header className="mb-4 border-b border-slate-300 pb-3">
        <h1 className="text-lg font-bold text-slate-900">Patient Demographics</h1>
        <p className="mt-1 text-[12px] text-slate-600">
          <span className="font-semibold text-slate-800">{patientName}</span>
          {" · "}
          MRN {dash(patient?.mrn)}
          {" · "}
          Printed {displayDateUS(new Date())}
        </p>
      </header>

      <PrintSection title="Personal Information">
        <FieldGrid>
          <PrintField
            label="Status"
            value={STATUS_LABELS[patient?.status] || patient?.status}
          />
          <PrintField
            label={SOC_DATE_LABEL}
            value={displayDateUS(patient?.admissionDate)}
          />
          {statusRequiresEocDate(patient?.status) ? (
            <PrintField
              label={EOC_DATE_LABEL}
              value={displayDateUS(patient?.eocDate)}
            />
          ) : null}
          <PrintField label="Last Name" value={patient?.lastName} />
          <PrintField label="First Name" value={patient?.firstName} />
          <PrintField label="Middle Name" value={patient?.middleName} />
          <PrintField
            label="Date of Birth"
            value={displayDateUS(patient?.dateOfBirth)}
          />
          <PrintField label="Gender" value={patient?.gender} />
          <PrintField label="Language" value={patient?.language} />
          <PrintField label="Marital Status" value={patient?.maritalStatus} />
          <PrintField label="Race" value={patient?.race} />
          <PrintField label="Religion" value={patient?.religion} />
        </FieldGrid>
      </PrintSection>

      <PrintSection title="Contact & Location">
        <FieldGrid>
          <PrintField
            label="Primary Phone"
            value={formatPhone(patient?.phone || "") || patient?.phone}
          />
          <PrintField
            label="Alternate Phone"
            value={
              formatPhone(patient?.alternatePhone || "") ||
              patient?.alternatePhone
            }
          />
          <PrintField label="Email" value={patient?.email} />
          <PrintField label="Street Address" value={patient?.address} />
          <PrintField label="Apt / Suite" value={patient?.addressLine2} />
          <PrintField label="City" value={patient?.city} />
          <PrintField label="State" value={patient?.state} />
          <PrintField label="Zip" value={patient?.zip} />
        </FieldGrid>
      </PrintSection>

      <PrintSection title={NUTRITION_SECTION_TITLE}>
        <DiagnosisNutritionPrintContent
          diagnoses={diagnoses}
          allergies={allergies}
          allergyName={allergyName}
          nutrition={nutrition}
          FieldGrid={FieldGrid}
          PrintField={PrintField}
          formatDietType={formatDietType}
          formatSupplementEnteral={formatSupplementEnteral}
          formatSodiumRestriction={formatSodiumRestriction}
          formatCarbohydrateRestriction={formatCarbohydrateRestriction}
        />
      </PrintSection>

      <PrintSection title="Referral Info">
        {!referrals.length ? (
          <EmptyLine text="No referral info added" />
        ) : (
          <div className="space-y-2">
            {referrals.map((row, idx) => (
              <div
                key={idx}
                className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 border-b border-slate-100 last:border-0 pb-2 last:pb-0"
              >
                <PrintField
                  label="Referral Date"
                  value={displayDateUS(row.referralDate)}
                />
                <PrintField label="Referral Source" value={row.referralSource} />
                <PrintField label="Referral Name" value={row.referralName} />
              </div>
            ))}
          </div>
        )}
      </PrintSection>

      <PrintSection title="Insurance Info">
        {!insurances.length ? (
          <EmptyLine text="No insurance info added" />
        ) : (
          <div className="space-y-2">
            {insurances.map((row, idx) => (
              <div
                key={idx}
                className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-x-4 gap-y-2 border-b border-slate-100 last:border-0 pb-2 last:pb-0"
              >
                <PrintField
                  label="Start Date"
                  value={displayDateUS(row.startDate)}
                />
                <PrintField label="Insurance Name" value={row.insuranceName} />
                <PrintField label="Policy #" value={row.policyNumber} />
                <PrintField label="Group" value={row.insuranceGroup} />
                <PrintField label="Co-Pay" value={row.patientCoPay} />
              </div>
            ))}
          </div>
        )}
      </PrintSection>

      <PrintSection title="Physician Info">
        {!physicians.length ? (
          <EmptyLine text="No physician info added" />
        ) : (
          <div className="space-y-2">
            {physicians.map((row, idx) => (
              <div
                key={idx}
                className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-x-4 gap-y-2 border-b border-slate-100 last:border-0 pb-2 last:pb-0"
              >
                <PrintField label="Type" value={row.physicianType} />
                <PrintField label="Name" value={row.physicianName} />
                <PrintField label="Address" value={row.physicianAddress} />
                <PrintField label="NPI" value={row.npiNumber} />
                <PrintField
                  label="PECOS Status"
                  value={
                    row.pecosStatus === "enrolled"
                      ? "Enrolled"
                      : row.pecosStatus === "un-enrolled"
                        ? "Un-enrolled"
                        : row.pecosStatus
                  }
                />
                <PrintField
                  label="Phone"
                  value={
                    formatPhone(row.physicianPhone || "") || row.physicianPhone
                  }
                />
                <PrintField
                  label="Fax"
                  value={
                    formatPhone(row.physicianFax || "") || row.physicianFax
                  }
                />
              </div>
            ))}
          </div>
        )}
      </PrintSection>

      <PrintSection title="Primary Pharmacy">
        <div className="space-y-2">
          <FieldGrid>
            <PrintField label="Pharmacy Name" value={patient?.pharmacyName} />
            <PrintField label="Pharmacy Type" value={patient?.pharmacyType} />
            <PrintField label="Address" value={patient?.pharmacyAddress} />
            <PrintField label="City" value={patient?.pharmacyCity} />
            <PrintField label="State" value={patient?.pharmacyState} />
            <PrintField label="ZIP" value={patient?.pharmacyZip} />
            <PrintField
              label="Phone"
              value={
                formatPhone(patient?.pharmacyPhone || "") ||
                patient?.pharmacyPhone
              }
            />
            <PrintField
              label="Fax"
              value={
                formatPhone(patient?.pharmacyFax || "") || patient?.pharmacyFax
              }
            />
          </FieldGrid>
        </div>
      </PrintSection>

      <PrintSection title="Primary Hospital">
        <div className="space-y-2">
          <FieldGrid>
            <PrintField label="Hospital Name" value={patient?.hospitalName} />
            <PrintField label="Hospital Type" value={patient?.hospitalType} />
            <PrintField label="Address" value={patient?.hospitalAddress} />
            <PrintField label="City" value={patient?.hospitalCity} />
            <PrintField label="State" value={patient?.hospitalState} />
            <PrintField label="ZIP" value={patient?.hospitalZip} />
            <PrintField
              label="Phone"
              value={
                formatPhone(patient?.hospitalPhone || "") ||
                patient?.hospitalPhone
              }
            />
            <PrintField
              label="Fax"
              value={
                formatPhone(patient?.hospitalFax || "") || patient?.hospitalFax
              }
            />
          </FieldGrid>
        </div>
      </PrintSection>

      <PrintSection title="Emergency Contacts">
        {!contacts.length ? (
          <EmptyLine text="No emergency contacts added" />
        ) : (
          <div className="space-y-2">
            {contacts.map((contact, idx) => {
              const fullName =
                [contact.lastName, contact.firstName]
                  .filter(Boolean)
                  .join(", ") || "";
              return (
                <div
                  key={idx}
                  className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 border-b border-slate-100 last:border-0 pb-2 last:pb-0"
                >
                  <PrintField label="Name" value={fullName} />
                  <PrintField label="Relation" value={contact.relationship} />
                  <PrintField
                    label="Phone"
                    value={formatPhone(contact.phone || "") || contact.phone}
                  />
                </div>
              );
            })}
          </div>
        )}
      </PrintSection>

      <PrintSection title="Notes">
        {!notes.length ? (
          <EmptyLine text="No notes yet" />
        ) : (
          <ul className="space-y-2">
            {notes.map((note) => {
              const body =
                String(note.description || "").trim() ||
                String(note.title || "").trim() ||
                "—";
              const when =
                displayDateUS(note.noteDate || note.createdAt) || "";
              const who = [
                note.createdByName || null,
                note.createdByRole ? `(${note.createdByRole})` : null,
              ]
                .filter(Boolean)
                .join(" ");
              return (
                <li
                  key={note.id || `${note.createdAt}-${body.slice(0, 12)}`}
                  className="border-b border-slate-100 last:border-0 pb-2 last:pb-0 break-inside-avoid"
                >
                  {note.title &&
                  String(note.title).trim() &&
                  String(note.title).trim() !== String(note.description || "").trim() ? (
                    <p className="text-[11px] font-semibold text-slate-700">
                      {note.title}
                    </p>
                  ) : null}
                  <p className="text-[12px] text-slate-900 whitespace-pre-wrap break-words">
                    {body}
                  </p>
                  <p className="mt-0.5 text-[10px] text-slate-500">
                    {[when, who].filter(Boolean).join(" · ") || "—"}
                  </p>
                </li>
              );
            })}
          </ul>
        )}
      </PrintSection>
    </article>
  );
};

DemographicsPrintSheet.propTypes = {
  patient: PropTypes.object.isRequired,
  notes: PropTypes.array,
};

export default DemographicsPrintSheet;
